import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Éditeur de lignes énergie pour le calcul du ratio Valeur de Production TICGN.
 *
 * Méthode officielle : Ratio = Σ (Consommation × Tarif fiscal) / Valeur_de_Production.
 *
 * - Charge les catégories fiscales depuis /api/tarifs-fiscaux-energies (avec auto-seed serveur)
 * - Le tarif fiscal est pré-rempli depuis la BDD mais reste modifiable manuellement
 * - L'utilisateur ajoute/supprime des lignes dynamiquement
 *
 * Props:
 *   - lignes:       tableau [{energie_code, libelle, consommation_mwh, tarif_fiscal}]
 *   - onChange:     callback(newLignes)
 *   - compact:      mode dense (utilisé pour la VP par SIRET) — défaut false
 *   - testIdPrefix: préfixe data-testid (défaut "vp-ligne")
 */
const LignesEnergiesEditor = ({ lignes = [], onChange, compact = false, testIdPrefix = 'vp-ligne' }) => {
  const [catalogue, setCatalogue] = useState([]);

  // Charge le catalogue une fois (côté admin il peut évoluer mais on n'a pas besoin de poll ici)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/tarifs-fiscaux-energies`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setCatalogue(Array.isArray(data.items) ? data.items : []);
        }
      } catch (_) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const tarifsMap = useMemo(() => {
    const m = {};
    catalogue.forEach((e) => { m[e.code] = e; });
    return m;
  }, [catalogue]);

  const updateLigne = useCallback((idx, patch) => {
    const next = lignes.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange(next);
  }, [lignes, onChange]);

  const removeLigne = useCallback((idx) => {
    onChange(lignes.filter((_, i) => i !== idx));
  }, [lignes, onChange]);

  const addLigne = useCallback(() => {
    const usedCodes = new Set(lignes.map((l) => l.energie_code));
    const next = catalogue.find((c) => !usedCodes.has(c.code)) || catalogue[0];
    onChange([
      ...lignes,
      next
        ? { energie_code: next.code, libelle: next.libelle, consommation_mwh: '', tarif_fiscal: next.tarif }
        : { energie_code: '', libelle: '', consommation_mwh: '', tarif_fiscal: '' },
    ]);
  }, [lignes, onChange, catalogue]);

  // Si la liste est vide à l'arrivée, on amorce avec une ligne « Gaz naturels » (TICGN focused)
  useEffect(() => {
    if (lignes.length === 0 && catalogue.length > 0) {
      const gaz = catalogue.find((c) => c.code === 'GAZ_NATUREL') || catalogue[0];
      onChange([{
        energie_code: gaz.code,
        libelle: gaz.libelle,
        consommation_mwh: '',
        tarif_fiscal: gaz.tarif,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue]);

  const totalCout = useMemo(() => lignes.reduce((s, l) => {
    const c = parseFloat(l.consommation_mwh) || 0;
    const t = parseFloat(l.tarif_fiscal) || 0;
    return s + c * t;
  }, 0), [lignes]);

  const inputCls = `${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} border border-purple-300 rounded bg-white w-full`;
  const labelCls = `${compact ? 'text-[10px]' : 'text-xs'} text-purple-900 mb-0.5 block`;

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      {lignes.map((l, idx) => {
        const sousTotal = (parseFloat(l.consommation_mwh) || 0) * (parseFloat(l.tarif_fiscal) || 0);
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`${testIdPrefix}-${idx}`}>
            <div className={compact ? 'col-span-4' : 'col-span-5'}>
              {idx === 0 && <label className={labelCls}>Catégorie fiscale</label>}
              <select
                value={l.energie_code || ''}
                onChange={(e) => {
                  const code = e.target.value;
                  const cat = tarifsMap[code];
                  updateLigne(idx, {
                    energie_code: code,
                    libelle: cat?.libelle || code,
                    // Auto-remplit le tarif seulement si vide ou si l'utilisateur n'a pas saisi de valeur custom
                    tarif_fiscal: cat?.tarif ?? l.tarif_fiscal,
                  });
                }}
                className={inputCls}
                data-testid={`${testIdPrefix}-${idx}-energie`}
              >
                <option value="">— Choisir —</option>
                {catalogue.map((c) => (
                  <option key={c.code} value={c.code}>{c.libelle}</option>
                ))}
              </select>
            </div>
            <div className="col-span-3">
              {idx === 0 && <label className={labelCls}>Consommation (MWh)</label>}
              <input
                type="number" min="0" step="0.01"
                value={l.consommation_mwh ?? ''}
                onChange={(e) => updateLigne(idx, { consommation_mwh: e.target.value })}
                className={inputCls}
                placeholder="0"
                data-testid={`${testIdPrefix}-${idx}-conso`}
              />
            </div>
            <div className="col-span-2">
              {idx === 0 && <label className={labelCls}>Tarif (€/MWh)</label>}
              <input
                type="number" min="0" step="0.001"
                value={l.tarif_fiscal ?? ''}
                onChange={(e) => updateLigne(idx, { tarif_fiscal: e.target.value })}
                className={inputCls}
                data-testid={`${testIdPrefix}-${idx}-tarif`}
              />
            </div>
            <div className="col-span-2">
              {idx === 0 && <label className={labelCls}>Coût fiscal (€)</label>}
              <div
                className={`${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} bg-purple-50 border border-purple-200 rounded text-right font-medium text-purple-900`}
                data-testid={`${testIdPrefix}-${idx}-soustotal`}
              >
                {sousTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              <button
                type="button" onClick={() => removeLigne(idx)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                data-testid={`${testIdPrefix}-${idx}-remove`}
                title="Supprimer la ligne"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button" onClick={addLigne}
          className={`${compact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5'} inline-flex items-center gap-1 border border-purple-300 text-purple-900 hover:bg-purple-100 rounded`}
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-4 h-4" /> Ajouter une énergie
        </button>
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-purple-900 font-semibold`} data-testid={`${testIdPrefix}-total`}>
          Σ Coût fiscal : <strong>{totalCout.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</strong>
        </div>
      </div>
    </div>
  );
};

export default LignesEnergiesEditor;
